import { findParentNodeClosestToPos } from "@tiptap/core";
import { Fragment, Slice } from "@tiptap/pm/model";
import { NodeSelection } from "@tiptap/pm/state";
import {
  generateId,
  TIPTAP_CAPTION_TYPE,
  TIPTAP_FIGURE_TYPE,
  TIPTAP_IMAGE_TYPE
} from "./../../ricos-content";
import { createRicosNodeExtension, Uploader } from "./../../plugin-commons";
import { findNodeById } from "./../../tiptap-editor/helpers";
import { tiptapImageAttributeDefaults } from "../consts";
import { ImagePluginService } from "../toolbar/imagePluginService";
import { Image } from "./image";
const imageTiptapExtension = createRicosNodeExtension({
  type: "react-node",
  name: TIPTAP_IMAGE_TYPE,
  groups: ["not-overtypable", "spoilerable", "resizable", "custom-styles", "shortcuts-enabled"],
  reconfigure: (config, _extensions, _props, settings, services) => ({
    ...config,
    addOptions: () => ({
      services,
      imagePluginService: new ImagePluginService(),
      ...settings
    })
  }),
  Component: Image,
  renderingOptions: {
    onDragStart({ editor, dragHandle, event }) {
      const target = event.target;
      if (!this.dom || this.contentDOM?.contains(target) || !dragHandle) {
        return;
      }
      if (this.dom === dragHandle) {
        event.dataTransfer?.setDragImage(this.dom, 0, 0);
      } else {
        const domBox = this.dom.getBoundingClientRect();
        const handleBox = dragHandle.getBoundingClientRect();
        const nativeEvent = "nativeEvent" in event ? event.nativeEvent : event;
        const x = handleBox.x - domBox.x + nativeEvent.offsetX;
        const y = handleBox.y - domBox.y + nativeEvent.offsetY;
        event.dataTransfer?.setDragImage(this.dom, x, y);
      }
      const selection = NodeSelection.create(editor.view.state.doc, this.getPos() - 1);
      const transaction = editor.view.state.tr.setSelection(selection);
      editor.view.dispatch(transaction);
    },
    stopEvent({ event }) {
      const target = event.target;
      const isInElement = this.dom.contains(target) && !this.contentDOM?.contains(target);
      if (!isInElement) {
        return false;
      }
      const isDragEvent = event.type.startsWith("drag");
      const isDropEvent = event.type === "drop";
      const isInput = ["INPUT", "BUTTON", "SELECT", "TEXTAREA"].includes(target.tagName) || target.isContentEditable;
      if (isInput && !isDropEvent && !isDragEvent) {
        return true;
      }
      const { isEditable } = this.editor;
      const { isDragging } = this;
      const isDraggable = true;
      const isSelectable = NodeSelection.isSelectable(this.node);
      const isCopyEvent = event.type === "copy";
      const isPasteEvent = event.type === "paste";
      const isCutEvent = event.type === "cut";
      const isClickEvent = event.type === "mousedown";
      if (!isDraggable && isSelectable && isDragEvent) {
        event.preventDefault();
      }
      if (isDraggable && isDragEvent && !isDragging) {
        event.preventDefault();
        return false;
      }
      if (isDraggable && isEditable && !isDragging && isClickEvent) {
        const dragHandle = target.closest("[data-drag-handle]");
        const isValidDragHandle = this.dom.closest(".node-IMAGE")?.contains(dragHandle);
        if (isValidDragHandle) {
          this.isDragging = true;
          const selection = NodeSelection.create(this.editor.view.state.doc, this.getPos());
          const transaction = this.editor.view.state.tr.setSelection(selection);
          this.editor.view.dispatch(transaction);
          this.editor.view.focus();
          document.addEventListener(
            "dragend",
            () => {
              this.isDragging = false;
            },
            { once: true }
          );
          document.addEventListener(
            "drop",
            () => {
              this.isDragging = false;
            },
            { once: true }
          );
          document.addEventListener(
            "mouseup",
            () => {
              this.isDragging = false;
            },
            { once: true }
          );
        }
      }
      if (isDragging || isDropEvent || isCopyEvent || isPasteEvent || isCutEvent || isClickEvent && isSelectable) {
        return false;
      }
      return true;
    }
  },
  createExtensionConfig({ Plugin, PluginKey }) {
    return {
      name: this.name,
      /*
       * Priority should be higher than ricos-common-plugin list-item extension in order
       * to shortcuts to work
       */
      priority: 102,
      group: "block",
      selectable: true,
      draggable: true,
      addAttributes: () => tiptapImageAttributeDefaults,
      addProseMirrorPlugins() {
        return [
          new Plugin({
            key: new PluginKey("handlePasteImage"),
            props: {
              transformCopied: (slice, { state }) => {
                const node = slice.content.firstChild;
                if (slice.content.size === 1 && node?.type.name === TIPTAP_IMAGE_TYPE) {
                  const imagePos = findNodeById(state.tr, node.attrs.id)[0]?.pos;
                  const figureNode = state.doc.resolve(imagePos).parent;
                  const newSlice = new Slice(
                    Fragment.from(figureNode),
                    slice.openStart,
                    slice.openEnd
                  );
                  return newSlice;
                }
                return slice;
              },
              handlePaste: (editor, event) => {
                const { state } = editor;
                const { selection } = state;
                const tr = state.tr;
                let pos = selection.$to.pos;
                let newSelection = null;
                if (selection.$to.node().type.name === TIPTAP_CAPTION_TYPE) {
                  const caption = findNodeById(state.tr, selection.$to.node().attrs.id)[0];
                  pos = caption.pos + selection.$to.node().nodeSize + 1;
                  newSelection = NodeSelection.create(state.doc, pos);
                } else if (selection.content().size === 1 && selection.content().content?.firstChild?.type.name === TIPTAP_IMAGE_TYPE) {
                  newSelection = NodeSelection.create(state.tr.doc, selection.$from.start() - 1);
                }
                if (!event?.clipboardData?.files?.length && newSelection) {
                  editor.dispatch(tr);
                  return true;
                }
                let hasFiles = false;
                if (event?.clipboardData?.types.includes("text/plain")) {
                  return false;
                }
                Array.from(event?.clipboardData?.files || []).filter((file) => file.type.startsWith("image")).forEach((file) => {
                  const imageNodeId = generateId();
                  this.editor.commands.insertContentAt(pos, {
                    type: TIPTAP_FIGURE_TYPE,
                    attrs: {
                      id: generateId()
                    },
                    content: [
                      {
                        type: TIPTAP_IMAGE_TYPE,
                        attrs: {
                          id: imageNodeId
                        }
                      },
                      {
                        type: TIPTAP_CAPTION_TYPE
                      }
                    ]
                  });
                  this.options.services?.uploadService?.uploadFile(
                    file,
                    imageNodeId,
                    new Uploader(this.options.handleFileUpload),
                    TIPTAP_FIGURE_TYPE,
                    this.options.imagePluginService
                  );
                  hasFiles = true;
                });
                if (hasFiles) {
                  event.preventDefault();
                  return true;
                }
                return false;
              }
            }
          })
        ];
      },
      addCommands() {
        return {
          setImageUrl: (url) => ({ commands }) => {
            return commands.updateAttributes(this.name, { image: { src: { custom: url } } });
          },
          setImageLoading: (loading) => ({ commands }) => {
            return commands.updateAttributes(this.name, { loading });
          }
        };
      },
      addKeyboardShortcuts() {
        const newLine = ({ editor }) => {
          const imageFigure = getImageFigure(editor);
          if (!imageFigure) {
            return false;
          }
          editor.chain().setTextSelection(imageFigure.start + imageFigure.node.nodeSize - 1).createParagraphNear().focus().run();
          return true;
        };
        const deleteFigure = ({ editor }) => {
          const selection = editor.state.selection;
          const selectedNode = editor.state.doc.resolve(selection.from).node();
          if (selectedNode.type.name === TIPTAP_FIGURE_TYPE) {
            editor.chain().focus().deleteNode(selectedNode.attrs.id).run();
            return true;
          }
          return false;
        };
        const tiptapKeyboardShortcuts = {
          Enter: newLine,
          Delete: deleteFigure,
          Backspace: deleteFigure
        };
        const newLineShortcuts = [];
        this.options.services.shortcuts.shortcuts.forEach((shortcut) => {
          if (shortcut.shortcut.name === "NewLine") {
            newLineShortcuts.push(shortcut);
          }
        });
        newLineShortcuts.forEach((newLineShortcut) => {
          const modifier = newLineShortcut.keys.macOs.modifiers[0];
          const key = newLineShortcut.keys.macOs.key;
          const shortcutKey = modifier ? `${modifier}-${key}` : key;
          tiptapKeyboardShortcuts[shortcutKey] = newLine;
        });
        return tiptapKeyboardShortcuts;
      }
    };
  }
});
const getImageFigure = (editor) => findParentNodeClosestToPos(
  editor.state.selection.$from,
  (node) => node.type.name === TIPTAP_FIGURE_TYPE
);
export {
  imageTiptapExtension
};
//# sourceMappingURL=image-tiptap-extension.js.map
